<template>
	<div
		class="min-h-screen bg-gradient-to-b from-white via-blue-50 to-white overflow-x-hidden"
	>
		<div class="fixed inset-0 pointer-events-none overflow-hidden">
			<div
				class="absolute -top-40 -right-32 w-80 h-80 bg-primary/8 rounded-full blur-2xl animate-pulse opacity-60"
			></div>
			<div
				class="absolute -bottom-32 -left-40 w-80 h-80 bg-brand-cyan/8 rounded-full blur-2xl animate-pulse delay-1000 opacity-60"
			></div>
		</div>

		<div class="relative z-10">
			<main class="w-full px-4 sm:px-6 py-8 sm:py-12 relative z-20">
				<div
					v-if="loading"
					class="flex justify-center items-center h-96"
				>
					<div class="text-center">
						<div class="relative w-16 h-16 mx-auto mb-4">
							<div
								class="absolute inset-0 border-4 border-blue-200 rounded-full"
							></div>
							<div
								class="absolute inset-0 border-t-4 border-primary rounded-full animate-spin"
							></div>
						</div>
						<p class="text-lg font-semibold text-text-primary">
							Preparing Your Options
						</p>
						<p class="text-text-secondary mt-2">
							Finding the perfect coverage...
						</p>
					</div>
				</div>

				<div
					v-else-if="currentStep === 'category'"
					class="space-y-6 sm:space-y-8"
				>
					<div
						class="w-full p-4 sm:p-6 lg:p-8 xl:p-10 bg-gradient-to-br from-white to-blue-50 border border-blue-200/50 rounded-2xl sm:rounded-3xl shadow-lg"
					>
						<div
							class="flex flex-col lg:flex-row items-start lg:items-center gap-4 sm:gap-6 lg:gap-8"
						>
							<div
								class="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-primary to-brand-navy rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg"
							>
								<svg
									class="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
									></path>
								</svg>
							</div>
							<div class="flex-1">
								<h2
									class="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-text-primary mb-2 sm:mb-3"
								>
									Welcome to Your Insurance Journey
								</h2>
								<p
									class="text-sm sm:text-base lg:text-lg text-text-secondary leading-relaxed"
								>
									Select a category below to discover
									insurance products specifically designed for
									your needs. Our expert team will guide you
									through every step.
								</p>
							</div>
						</div>
					</div>

					<div class="mt-6">
						<div class="w-full px-2">
							<CategoryCards
								:categories="InsuranceModel.categories"
								@category-selected="setCategory"
							/>
						</div>
					</div>
				</div>

				<div v-else-if="currentStep === 'product'" class="space-y-8">
					<div
						class="p-8 lg:p-10 bg-gradient-to-r from-emerald-50 to-green-50 border border-green-200/50 rounded-3xl shadow-lg"
					>
						<div
							class="flex flex-col sm:flex-row items-start sm:items-center gap-6"
						>
							<div
								class="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg"
							>
								<svg
									class="w-8 h-8 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2.5"
										d="M5 13l4 4L19 7"
									></path>
								</svg>
							</div>
							<div class="flex-1">
								<h3
									class="text-2xl font-bold text-green-900 mb-1"
								>
									Perfect Choice!
								</h3>
								<p class="text-green-700 text-lg">
									Category:
									<span class="font-semibold text-primary">{{
										selectedCategoryLabel
									}}</span>
								</p>
							</div>
							<button
								@click="goBack"
								class="px-6 py-2.5 bg-white text-green-700 font-semibold rounded-lg border-2 border-green-200 hover:bg-green-50 transition-all whitespace-nowrap"
							>
								← Change Category
							</button>
						</div>
					</div>

					<!-- <ProductList
						:products="filteredProducts"
						@back="goBack"
						@product-selected="handleProductSelected"
					/> -->
				</div>
			</main>
		</div>

		<transition name="modal-mobile">
			<div
				v-if="showHelpModal"
				class="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
				@click.self="showHelpModal = false"
			>
				<div
					class="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl sm:shadow-xl w-full sm:max-w-md overflow-y-auto transition-all duration-300 transform"
					:class="{
						'max-h-[90vh] mt-0 sm:mt-0': true,
						'sm:max-h-[90vh]': true,
						'rounded-b-none sm:rounded-b-xl': true,
					}"
					@click.stop
				>
					<!-- Mobile drag handle -->
					<div class="flex justify-center py-2 sm:hidden">
						<div class="w-12 h-1 bg-gray-300 rounded-full"></div>
					</div>

					<div
						class="relative p-5 bg-gradient-to-r from-primary to-brand-navy text-white rounded-t-xl sm:rounded-t-xl"
					>
						<button
							@click="showHelpModal = false"
							class="absolute top-4 right-4 text-white p-1 -mr-1"
							aria-label="Close"
						>
							<svg
								class="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								></path>
							</svg>
						</button>
						<div>
							<h3 class="text-xl font-bold sm:text-2xl">
								Need Help?
							</h3>
							<p class="text-white/90 text-sm sm:text-base mt-1">
								Our team is here to assist you
							</p>
						</div>
					</div>

					<div class="p-3 sm:p-4 md:p-5 space-y-3 sm:space-y-4">
						<div
							class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
						>
							<div
								class="group p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-white border border-gray-100 rounded-lg hover:border-primary/40 hover:shadow-sm transition-all"
							>
								<div
									class="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-brand-navy rounded-lg flex items-center justify-center mb-2 sm:mb-2.5 group-hover:scale-105 transition-transform"
								>
									<svg
										class="w-5 h-5 sm:w-6 sm:h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
										></path>
									</svg>
								</div>
								<p
									class="text-xs sm:text-sm font-semibold text-text-secondary mb-1 sm:mb-2"
								>
									Call Us
								</p>
								<p
									class="text-base sm:text-lg font-bold text-primary mb-1"
								>
									<a
										href="tel:+254723350348"
										aria-label="Call us at 0723 350 348"
										class="inline-block text-primary hover:underline"
									>
										0723 350 348
									</a>
								</p>
								<p class="text-xs text-text-secondary">
									Available 24/7
								</p>
							</div>

							<!-- Email Us Card -->
							<div
								class="group p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-white border border-gray-100 rounded-lg hover:border-primary/40 hover:shadow-sm transition-all"
							>
								<div
									class="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-accent to-brand-navy rounded-lg flex items-center justify-center mb-2 sm:mb-2.5 group-hover:scale-105 transition-transform"
								>
									<svg
										class="w-5 h-5 sm:w-6 sm:h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
										></path>
									</svg>
								</div>
								<p
									class="text-xs sm:text-sm font-semibold text-text-secondary mb-1 sm:mb-2"
								>
									Email Us
								</p>
								<p
									class="text-sm sm:text-base md:text-lg font-bold text-primary mb-1 break-all"
								>
									<a
										href="mailto:corporate@nexus.ke"
										aria-label="Email us at corporate@nexus.ke"
										class="inline-block text-primary hover:underline"
									>
										corporate@nexus.ke
									</a>
								</p>
								<p class="text-xs text-text-secondary">
									Response within 2 hours
								</p>
							</div>

							<!-- Visit Us Card -->
							<div
								class="group p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-white border border-gray-100 rounded-lg hover:border-primary/40 hover:shadow-sm transition-all sm:col-span-2 lg:col-span-1"
							>
								<div
									class="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-brand-cyan to-primary rounded-lg flex items-center justify-center mb-2 sm:mb-2.5 group-hover:scale-105 transition-transform"
								>
									<svg
										class="w-5 h-5 sm:w-6 sm:h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
										></path>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
										></path>
									</svg>
								</div>
								<p
									class="text-xs sm:text-sm font-semibold text-text-secondary mb-1 sm:mb-2"
								>
									Visit Us
								</p>
								<p
									class="text-base sm:text-lg font-bold text-primary mb-1"
								>
									Lavington West
								</p>
								<p
									class="text-xs sm:text-sm text-text-secondary"
								>
									42A Nairobi, Kenya
								</p>
							</div>
						</div>
					</div>

					<!-- Modal Footer -->
					<div
						class="px-3 sm:px-4 md:px-5 py-3 sm:py-4 bg-gray-50 border-t border-gray-100 rounded-b-xl flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3"
					>
						<p
							class="text-xs sm:text-sm text-text-secondary text-center sm:text-left"
						>
							We typically respond within 2 hours
						</p>
						<button
							@click="showHelpModal = false"
							class="w-full sm:w-auto px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-primary to-brand-navy text-white text-sm font-medium rounded-md hover:shadow-md active:scale-95 transition-all"
						>
							Close
						</button>
					</div>
				</div>
			</div>
		</transition>

		<!-- Floating Help Button -->
		<div class="fixed z-40 bottom-6 right-4 sm:bottom-8 sm:right-8">
			<button
				@click="showHelp"
				class="group relative flex items-center justify-center text-white rounded-full shadow-2xl w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary to-brand-navy hover:shadow-2xl hover:shadow-primary/40 hover:scale-110 transition-all active:scale-95"
				aria-label="Get help"
			>
				<svg
					class="w-8 h-8 group-hover:scale-110 transition-transform"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
					></path>
				</svg>
				<div
					class="absolute inset-0 rounded-full border-2 border-primary opacity-0 animate-pulse"
				></div>
			</button>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import CategoryCards from './CategoryCards.vue';
import ProductList from './ProductList.vue';

export default {
	name: 'InsuranceFlow',
	components: {
		CategoryCards,
		ProductList,
	},
	data() {
		return {
			showHelpModal: false,
		};
	},
	computed: {
		...mapGetters('insurance', [
			'InsuranceModel',
			'currentStep',
			'selectedCategory',
			'filteredProducts',
			'loading',
		]),
		selectedCategoryLabel() {
			const category = this.InsuranceModel.categories.find(
				(cat) => cat.value === this.selectedCategory,
			);
			return category ? category.label : '';
		},
	},
	watch: {
		currentStep(newStep) {
			if (newStep === 'product') {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}
		},
	},
	methods: {
		...mapActions('insurance', ['setCategory', 'setProduct', 'goBack']),
		handleProductSelected(product) {
			this.setProduct(product);
		},
		showHelp() {
			this.showHelpModal = true;
		},
	},
	created() {
		try {
			if (!this.$store._modulesNamespaceMap['insurance/']) {
				this.$store.registerModule(
					'insurance',
					require('./store').default,
				);
			}
		} catch (error) {
			console.error('Error registering insurance module:', error);
		}
		this.$store.commit('insurance/resetInsuranceFlow');
	},
	mounted() {
		if (typeof Event !== 'undefined') {
			Event.$emit('loader:show', false);
		}
	},
	beforeDestroy() {
		try {
			if (this.$store._modulesNamespaceMap['insurance/']) {
				this.$store.unregisterModule('insurance');
			}
		} catch (error) {
			console.error('Error unregistering insurance module:', error);
		}
	},
};
</script>

<style scoped>
/* Modal transitions */
.modal-mobile-enter-active,
.modal-mobile-leave-active {
	transition:
		opacity 0.3s,
		transform 0.3s;
}

.modal-mobile-enter-from,
.modal-mobile-leave-to {
	opacity: 0;
	transform: translateY(20px);
}

/* For mobile devices */
@media (max-width: 640px) {
	.modal-mobile-enter-from,
	.modal-mobile-leave-to {
		transform: translateY(100%);
	}
}

@keyframes pulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
}

.animate-pulse {
	animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.delay-1000 {
	animation-delay: 1000ms;
}

.fade-enter-active,
.fade-leave-active {
	transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
